import { sortByName } from '../utils/sortOptions';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  MagnifyingGlassIcon, ChevronRightIcon,
  AcademicCapIcon, BanknotesIcon,
} from '@heroicons/react/24/outline';

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .sr-root * { box-sizing:border-box; }
  .sr-root { font-family:'Sora',sans-serif; color:#1a1d2e; animation:srFade .4s ease both; }
  @keyframes srFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .sr-header { margin-bottom:1.75rem; }
  .sr-title  { font-size:1.6rem; font-weight:700; letter-spacing:-.03em; color:#0f1123; }
  .sr-sub    { font-size:.78rem; color:#9094a8; margin-top:3px; }
  .sr-search-wrap { position:relative; margin-bottom:1.5rem; }
  .sr-search-wrap svg { position:absolute; left:14px; top:50%; transform:translateY(-50%); width:16px; height:16px; color:#9094a8; stroke-width:2; pointer-events:none; }
  .sr-search { font-family:'Sora',sans-serif; font-size:.84rem; width:100%; max-width:360px; padding:11px 14px 11px 40px; border:1.5px solid #e4e7f0; border-radius:12px; background:white; color:#1a1d2e; outline:none; transition:border-color .2s,box-shadow .2s; }
  .sr-search:focus { border-color:#0d9488; box-shadow:0 0 0 3px rgba(13,148,136,.09); }
  .sr-search::placeholder { color:#c0c5dc; }
  .admin-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(280px,1fr)); gap:1rem; }
  .admin-card { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:1.3rem 1.4rem; cursor:pointer; position:relative; overflow:hidden; transition:transform .18s,box-shadow .18s,border-color .18s; animation:srFade .4s ease both; }
  .admin-card:hover { transform:translateY(-2px); box-shadow:0 8px 28px rgba(13,148,136,.1); border-color:#99f6e4; }
  .admin-card::after { content:''; position:absolute; top:0; right:0; width:80px; height:80px; border-radius:0 16px 0 80px; background:#0d9488; opacity:.04; }
  .ac-top    { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1rem; }
  .ac-avatar { width:44px; height:44px; border-radius:12px; background:#f0fdfa; color:#0d9488; display:flex; align-items:center; justify-content:center; font-size:.9rem; font-weight:700; text-transform:uppercase; flex-shrink:0; }
  .ac-arrow  { width:28px; height:28px; border-radius:8px; background:#f0f2f7; color:#9094a8; display:flex; align-items:center; justify-content:center; transition:background .15s,color .15s; }
  .ac-arrow svg { width:14px; height:14px; stroke-width:2.5; }
  .admin-card:hover .ac-arrow { background:#f0fdfa; color:#0d9488; }
  .ac-name  { font-size:.9rem; font-weight:700; color:#0f1123; margin-bottom:2px; }
  .ac-email { font-size:.72rem; color:#9094a8; margin-bottom:10px; }
  .ac-chips { display:flex; gap:6px; flex-wrap:wrap; }
  .ac-chip  { display:inline-flex; align-items:center; gap:4px; font-size:.68rem; font-weight:600; padding:3px 9px; border-radius:99px; }
  .ac-chip svg { width:11px; height:11px; stroke-width:2; }
  .ac-chip.emp    { background:#fffbeb; color:#92400e; }
  .ac-chip.salary { background:#f0fdfa; color:#065f46; font-family:'JetBrains Mono',monospace; }
  .sr-empty { text-align:center; padding:5rem 2rem; }
  .sr-empty-icon { width:56px; height:56px; border-radius:14px; background:#f0fdfa; color:#0d9488; display:flex; align-items:center; justify-content:center; margin:0 auto 14px; }
  .sr-empty-icon svg { width:26px; height:26px; stroke-width:1.6; }
  .sr-empty-title { font-size:.95rem; font-weight:600; color:#1a1d2e; margin-bottom:5px; }
  .sr-empty-sub   { font-size:.78rem; color:#9094a8; }
  .sr-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; font-size:.88rem; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#0d9488; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }
  @media (max-width:768px) { .sr-title { font-size:1.2rem; } .sr-search { max-width:100%; } }
`;

const initials = (name='') => name.split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase()||'?';
const fmt = (n) => n > 0 ? `Rs. ${Number(n).toLocaleString('en-PK',{maximumFractionDigits:0})}` : null;

export default function OwnerAdminSalary() {
  const navigate  = useNavigate();
  const [schools,  setSchools]  = useState([]);
  const [schoolId, setSchoolId] = useState('');
  const [admins,   setAdmins]   = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [search,   setSearch]   = useState('');

  // Load schools once
  useEffect(() => {
    api.get('/owner/schools').then(r => {
      const s = r.data?.schools || r.data || [];
      setSchools(Array.isArray(s) ? s : []);
      if (s.length) setSchoolId(s[0]._id);
    }).catch(() => {});
  }, []);

  // Load admins when school changes
  useEffect(() => {
    if (!schoolId) return;
    setLoading(true);
    api.get(`/owner/branches/${schoolId}/admins`)
      .then(r => setAdmins(Array.isArray(r.data) ? r.data : []))
      .catch(() => toast.error('Failed to load admins'))
      .finally(() => setLoading(false));
  }, [schoolId]);

  const filtered = admins.filter(a => {
    if (!search) return true;
    const q = search.toLowerCase();
    return a.name?.toLowerCase().includes(q) || a.email?.toLowerCase().includes(q);
  });

  return (
    <div className="sr-root">
      {/* School selector */}
      <div style={{marginBottom:'1rem'}}>
        <select value={schoolId} onChange={e => setSchoolId(e.target.value)}
          style={{minWidth:220,padding:'9px 12px',border:'1.5px solid #e0e4f0',borderRadius:10,fontFamily:'Sora',fontSize:'.82rem',background:'white',outline:'none'}}>
          <option value="">Select School…</option>
          {sortByName(schools).map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
        </select>
      </div>

      <style>{style}</style>

      <div className="sr-header">
        <h1 className="sr-title">Admin Salary Records</h1>
        <p className="sr-sub">Select an admin to view and manage their monthly salary.</p>
      </div>

      <div className="sr-search-wrap">
        <MagnifyingGlassIcon />
        <input className="sr-search" placeholder="Search by name or email…"
          value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {!schoolId ? (
        <div className="sr-empty">
          <div className="sr-empty-icon"><AcademicCapIcon /></div>
          <div className="sr-empty-title">Select a school</div>
          <div className="sr-empty-sub">Choose a school above to view its admins.</div>
        </div>
      ) : loading ? (
        <div className="sr-loading"><div className="spinner"/>Loading admins…</div>
      ) : filtered.length === 0 ? (
        <div className="sr-empty">
          <div className="sr-empty-icon"><AcademicCapIcon /></div>
          <div className="sr-empty-title">{search ? 'No admins found' : 'No admins yet'}</div>
          <div className="sr-empty-sub">{search ? 'Try a different search.' : 'Admins will appear here once added.'}</div>
        </div>
      ) : (
        <div className="admin-grid">
          {filtered.map((a, i) => (
            <div key={a._id} className="admin-card"
              style={{ animationDelay:`${i*.04}s` }}
              onClick={() => navigate(`/owner/admin-salary/${schoolId}/${a._id}`)}>
              <div className="ac-top">
                <div className="ac-avatar">{initials(a.name)}</div>
                <div className="ac-arrow"><ChevronRightIcon /></div>
              </div>
              <div className="ac-name">{a.name}</div>
              <div className="ac-email">{a.email}</div>
              <div className="ac-chips">
                {a.employeeId && <span className="ac-chip emp"><AcademicCapIcon />{a.employeeId}</span>}
                {/* monthlySalary from User model */}
                {a.monthlySalary > 0 && <span className="ac-chip salary"><BanknotesIcon />{fmt(a.monthlySalary)}/mo</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}